"use client";
import { Card, Checkbox, Col, Divider, Row, Typography } from "antd";
import Image from "next/image";
import { useParams, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import BookingStatus from "../../../Components/Bookings/BookingStatus/BookingStatus";
import CustomDropDown from "../../../Components/CustomDropDown/CustomDropDown";
import ServiceRequestFields from "../../../Components/ServiceRequest/ServiceRequestFields";
import TripSummaryTrainInfoCard from "../../../Components/TrainOffer/TrainInfoCard/TrainInfoCard";
import { _eventBus } from "../../../core/events/EventBus";
import {
  isEmpty,
  isWithoutBookingServiceRequest,
} from "../../../Lib/Utils/validationUtil";
import { srReqClaimTree } from "../../../Orchestrator/ConstantTree/ServiceRequestClaim/ServiceRequestClaim";
import {
  updateRecoilState,
  useRecState,
} from "../../../Orchestrator/Recoils/Events/Recoil";
import {
  ISelectedCategoryAndSubCategory,
  IServiceRequestCustom,
  ISubServiceRequestCategory,
} from "../../../Orchestrator/Types/Interface/IServiceRequest";
import { TrainType } from "../../../Orchestrator/Types/Types/TrainTypes";
import "./page.css";
import { TrainTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";

const ServiceRequestClaim = () => {
  const intl = useIntl();
  const params = useParams();
  const searchParams = useSearchParams();
  const [outboundTrain] = useRecState<TrainType>("selectedTrainStateAtom");
  const [inboundTrain] = useRecState<TrainType>(
    "selectedInboundTrainStateAtom"
  );
  const [serviceCategories] = useRecState<IServiceRequestCustom[]>(
    "serviceCategoriesAtom"
  );
  const [lastPageVisited] = useRecState<string>(
    "lastVisitedPageForServiceRequestAtom"
  );
  const [subServiceCategories] = useRecState<ISubServiceRequestCategory[]>(
    "subServiceCategoriesAtom"
  );
  const [selectedTripForServiceRequest] = useRecState<string[]>(
    "selectedTripForServiceRequestAtom"
  );
  const [selectedCategoryAndSubCategory] =
    useRecState<ISelectedCategoryAndSubCategory>(
      "selectedCategoryAndSubCategoryAtom"
    );

  const isOnlyServiceRequest: boolean = isWithoutBookingServiceRequest(
    (params?.booking_id || "") as string
  );

  useEffect(() => {
    const evt = _eventBus.on<void, void>({
      eventName: srReqClaimTree.RequestClaim.FlowStarted,
      handler: async () => {
        if (!isEmpty(params?.booking_id)) {
          const bookingId = params.booking_id as string;
          if (!isWithoutBookingServiceRequest(bookingId)) {
            const direction = searchParams.get("direction") || "";
            _eventBus.emit({
              eventName: srReqClaimTree.Booking.GetDetails,
              data: {
                bookingId,
                direction,
              },
            });
          } else {
            _eventBus.emit({
              eventName: srReqClaimTree.Category.Get,
              data: "NO_BOOKING",
            });
          }
        }
      },
    });

    /**
     * This is because, to unregister @event srReqClaimTree.RequestClaim.FlowStarted
     * Once page is unmount.
     */
    return () => {
      updateRecoilState("selectedTripForServiceRequestAtom", []);
      updateRecoilState("selectedCategoryAndSubCategoryAtom", []);
      updateRecoilState("bookingResponseAtom", null);
      updateRecoilState("selectedDateAtom", null);
      updateRecoilState("selectedTrainStateAtom", null);
      updateRecoilState("selectedInboundTrainAtom", null);
      updateRecoilState("selectedInboundTrainStateAtom", null);
      updateRecoilState("serviceCategoriesAtom", []);
      updateRecoilState("subServiceCategoriesAtom", []);
      evt.off();
    };
  }, []);

  const handleCategoryAndSubCategoryOnChange = (
    value: string,
    type: "category" | "subCategory"
  ) => {
    _eventBus.emit({
      eventName: srReqClaimTree.Category.onChange,
      data: {
        type,
        value,
        selectedCategoryAndSubCategory,
        serviceCategories,
      },
    });
  };

  return (
    <div className="service-request container">
      <div className="mt-20 mb-20">
        <>
          <Row className="request-mobile-view">
            <Col span={24}>
              <div className="align-items-center mt-20 mb-10 ff-inter">
                <div
                  className="align-items-center cursor-pointer"
                  onClick={() => {
                    _eventBus.emit({
                      eventName: srReqClaimTree.Reset.Atom,
                      data: { backClick: "backClick", lastPageVisited },
                    });
                  }}
                >
                  <span className="mr-5 cursor-pointer d-flex back-btn-arrow">
                    <Image
                      src="/assets/booking/arrow-left.svg"
                      width={14}
                      height={14}
                      alt="add_calendar"
                    />
                  </span>
                  <span className="fs-16">
                    {intl.formatMessage({ id: "backText" })}
                  </span>
                </div>
              </div>
            </Col>
            <Col span={22}>
              <Typography.Title level={3} className="fw-300 fs-32">
                {intl.formatMessage({ id: "createServiceRequest" })}
              </Typography.Title>
            </Col>
          </Row>
          <Row gutter={20} className="service-request-mobile">
            <Col span={11}>
              <Card className="left-card">
                {(outboundTrain || inboundTrain) && (
                  <>
                    <Row className="align-items-center booking-reference justify-content-between">
                      <div>
                        <Typography.Text className="fs-18 fw-600 color-757575 mr-10">
                          {intl.formatMessage({ id: "bookingReferenceText" })}
                        </Typography.Text>
                        <Typography.Text className="fs-18 fw-600">
                          {!isEmpty(outboundTrain?.bookingCode)
                            ? `#${outboundTrain.bookingCode}`
                            : !isEmpty(inboundTrain?.bookingCode) &&
                              `#${inboundTrain.bookingCode}`}
                        </Typography.Text>
                      </div>
                      <BookingStatus />
                    </Row>
                    {outboundTrain && (
                      <Row
                        className={`${
                          inboundTrain &&
                          selectedTripForServiceRequest.includes(
                            TrainTypeEnum.outbound
                          )
                            ? "trip-detail-red"
                            : "trip-detail"
                        }`}
                      >
                        <Col span={24}>
                          <div className="align-items-center ml-10 mt-5">
                            {inboundTrain && outboundTrain && (
                              <Checkbox
                                id="outbound-chk"
                                checked={selectedTripForServiceRequest.includes(
                                  TrainTypeEnum.outbound
                                )}
                                className="mr-5"
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    updateRecoilState(
                                      "selectedTripForServiceRequestAtom",
                                      [
                                        ...selectedTripForServiceRequest,
                                        TrainTypeEnum.outbound,
                                      ]
                                    );
                                  } else {
                                    updateRecoilState(
                                      "selectedTripForServiceRequestAtom",
                                      [...selectedTripForServiceRequest].filter(
                                        (d) => d !== TrainTypeEnum.outbound
                                      )
                                    );
                                  }
                                }}
                              />
                            )}
                            <Typography.Text
                              className={`${
                                inboundTrain && "ml-10 "
                              }fs-16 fw-700 ff-inter`}
                            >
                              {intl.formatMessage({ id: "outbound" })}
                            </Typography.Text>
                          </div>
                          <Divider style={{ margin: "5px 0px 15px 0px" }} />
                        </Col>
                        <Col span={24}>
                          <TripSummaryTrainInfoCard
                            type={TrainTypeEnum.outbound}
                            screen="serviceRequest"
                          />
                        </Col>
                      </Row>
                    )}
                    {inboundTrain && (
                      <Row
                        className={`${
                          outboundTrain &&
                          selectedTripForServiceRequest.includes(
                            TrainTypeEnum.inbound
                          )
                            ? "trip-detail-red"
                            : "trip-detail"
                        } mt-20`}
                      >
                        <Col span={24}>
                          <div className="align-items-center ml-10 mt-5">
                            {inboundTrain && outboundTrain && (
                              <Checkbox
                                id="inbound-chk"
                                checked={selectedTripForServiceRequest.includes(
                                  TrainTypeEnum.inbound
                                )}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    updateRecoilState(
                                      "selectedTripForServiceRequestAtom",
                                      [
                                        ...selectedTripForServiceRequest,
                                        TrainTypeEnum.inbound,
                                      ]
                                    );
                                  } else {
                                    updateRecoilState(
                                      "selectedTripForServiceRequestAtom",
                                      [...selectedTripForServiceRequest].filter(
                                        (d) => d !== TrainTypeEnum.inbound
                                      )
                                    );
                                  }
                                }}
                              />
                            )}
                            <Typography.Text
                              className={`${
                                outboundTrain && "ml-10 "
                              }fs-16 fw-700 ff-inter`}
                            >
                              {intl.formatMessage({ id: "inbound" })}
                            </Typography.Text>
                          </div>
                          <Divider style={{ margin: "5px 0px 15px 0px" }} />
                        </Col>
                        <Col span={24}>
                          <TripSummaryTrainInfoCard
                            type={TrainTypeEnum.inbound}
                            screen="serviceRequest"
                          />
                        </Col>
                      </Row>
                    )}
                  </>
                )}
                <Row>
                  <Col span={24} className="mt-20">
                    <Typography.Text className="fs-16 fw-700 ff-inter">
                      {intl.formatMessage({ id: "selectServiceRequestText" })}
                    </Typography.Text>
                  </Col>
                </Row>
                <Row>
                  <Col span={24} className="mt-20">
                    <Typography.Text className="fs-16 fw-700 ff-inter">
                      {intl.formatMessage({ id: "categoryText" })}
                    </Typography.Text>
                  </Col>
                  <Col span={24} className="mt-5">
                    <CustomDropDown
                      value={selectedCategoryAndSubCategory?.category || ""}
                      Options={serviceCategories.map(
                        (d: IServiceRequestCustom) => {
                          return {
                            labelTranslationKey: d.categoryName,
                            value: d.categoryName,
                          };
                        }
                      )}
                      onChange={(value: string, item: any) => {
                        handleCategoryAndSubCategoryOnChange(value, "category");
                      }}
                      placeHolderTranslationKey={intl.formatMessage({
                        id: "selectCategoryText",
                      })}
                      disabled={
                        isOnlyServiceRequest
                          ? false
                          : selectedTripForServiceRequest.length == 0
                      }
                    />
                  </Col>
                </Row>
                {/* {subServiceCategories.length > 0 && subServiceCategories != null && ( */}
                  {(selectedCategoryAndSubCategory?.category != "OTHERS" || selectedCategoryAndSubCategory?.subCategory != "OTHERS") && (
                  <Row className="mt-10">
                    <Col span={24}>
                      <Typography.Text className="fs-16 fw-700 ff-inter">
                        {intl.formatMessage({ id: "subCategoryText" })}
                      </Typography.Text>
                    </Col>
                    <Col span={24} className="mt-5">
                      <CustomDropDown
                        value={
                          selectedCategoryAndSubCategory?.subCategory || ""
                        }
                        Options={subServiceCategories.map(
                          (d: ISubServiceRequestCategory) => {
                            return {
                              labelTranslationKey: d.name,
                              value: d.name,
                            };
                          }
                        )}
                        onChange={(value: string, item: any) => {
                          handleCategoryAndSubCategoryOnChange(
                            value,
                            "subCategory"
                          );
                        }}
                        placeHolderTranslationKey={
                          isEmpty(selectedCategoryAndSubCategory.category)
                            ? intl.formatMessage({
                                id: "selectCategoryFirstText",
                              })
                            : intl.formatMessage({
                                id: "selectSubCategoryText",
                              })
                        }
                        disabled={
                          isEmpty(selectedCategoryAndSubCategory.category) ||
                          (isOnlyServiceRequest
                            ? false
                            : selectedTripForServiceRequest.length == 0)
                        }
                      />
                    </Col>
                  </Row>
                )}
              </Card>
            </Col>
            <Col span={13}>
              <ServiceRequestFields />
            </Col>
          </Row>
        </>
      </div>
    </div>
  );
};

export default ServiceRequestClaim;
